// A photo's own details, kept when the photo is shrunk for upload: the time
// it was taken (and its UTC offset) and the phone's make and model. They help
// with carrier claims ("taken at 13:42 on 28 Oct on a Galaxy Z Fold"). The
// GPS location and everything else the phone stores are left out.
//
// Phones save these in the JPEG's Exif block (an APP1 segment near the start
// of the file). readPhotoDetails reads them from a photo's first bytes;
// withPhotoDetails writes them into a new JPEG as a small Exif block of its
// own. Neither ever throws: a file they can't read gives null, and a JPEG they
// can't add to comes back as it was.

const EXIF_HEADER = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00]; // "Exif\0\0"

const TAG = {
  make: 0x010f,
  model: 0x0110,
  dateTime: 0x0132,
  exifIfd: 0x8769,
  exifVersion: 0x9000,
  takenAt: 0x9003,
  offset: 0x9010,
  takenAtOffset: 0x9011,
};
const ASCII = 2;
const LONG = 4;
const UNDEFINED = 7;

const TAKEN_AT = /^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}$/;
const OFFSET = /^[+-]\d{2}:\d{2}$/;
// Make and model are short names; anything longer isn't one.
const NAME_LIMIT = 64;

// How many bytes from the start of a photo to read for its details: the Exif
// block is near the start and is never more than 64 KB.
export const PHOTO_DETAILS_BYTES = 128 * 1024;

const isExifAt = (bytes, at) =>
  EXIF_HEADER.every((byte, index) => bytes[at + index] === byte);

// The details in a TIFF block (the body of an Exif segment) that starts at
// `start` and ends before `end`.
const readTiff = (bytes, start, end) => {
  const little = bytes[start] === 0x49 && bytes[start + 1] === 0x49; // "II"
  const big = bytes[start] === 0x4d && bytes[start + 1] === 0x4d; // "MM"
  if (!little && !big) return null;
  const u16 = (at) =>
    little
      ? bytes[at] | (bytes[at + 1] << 8)
      : (bytes[at] << 8) | bytes[at + 1];
  const u32 = (at) =>
    little
      ? (bytes[at] |
          (bytes[at + 1] << 8) |
          (bytes[at + 2] << 16) |
          (bytes[at + 3] << 24)) >>>
        0
      : ((bytes[at] << 24) |
          (bytes[at + 1] << 16) |
          (bytes[at + 2] << 8) |
          bytes[at + 3]) >>>
        0;
  if (start + 8 > end || u16(start + 2) !== 42) return null;

  const readIfd = (offset) => {
    const entries = {};
    const at = start + offset;
    if (offset < 8 || at + 2 > end) return entries;
    const count = u16(at);
    for (let index = 0; index < count; index += 1) {
      const entry = at + 2 + index * 12;
      if (entry + 12 > end) break;
      entries[u16(entry)] = {
        type: u16(entry + 2),
        count: u32(entry + 4),
        valueAt: entry + 8,
      };
    }
    return entries;
  };
  const text = (entry) => {
    if (!entry || entry.type !== ASCII || !entry.count) return "";
    const at = entry.count <= 4 ? entry.valueAt : start + u32(entry.valueAt);
    if (at < start || at + entry.count > end) return "";
    let value = "";
    for (let index = 0; index < entry.count; index += 1) {
      const code = bytes[at + index];
      if (code === 0) break;
      value += String.fromCharCode(code);
    }
    return value.trim();
  };

  const main = readIfd(u32(start + 4));
  const pointer = main[TAG.exifIfd];
  const exif =
    pointer && pointer.type === LONG ? readIfd(u32(pointer.valueAt)) : {};
  const takenAt = [text(exif[TAG.takenAt]), text(main[TAG.dateTime])].find(
    (value) => TAKEN_AT.test(value),
  );
  const offset = [text(exif[TAG.takenAtOffset]), text(exif[TAG.offset])].find(
    (value) => OFFSET.test(value),
  );
  const name = (value) => (value.length <= NAME_LIMIT ? value : "");
  const details = {
    takenAt: takenAt ?? "",
    offset: takenAt ? (offset ?? "") : "",
    make: name(text(main[TAG.make])),
    model: name(text(main[TAG.model])),
  };
  return details.takenAt || details.make || details.model ? details : null;
};

/**
 * The details in a JPEG's first bytes (a Uint8Array): { takenAt, offset,
 * make, model } as the phone wrote them ("2026:10:28 13:42:31", "-04:00",
 * "samsung", "Galaxy Z Fold8 Ultra"; "" when missing), or null when the
 * bytes aren't a JPEG or carry none of them.
 */
export const readPhotoDetails = (bytes) => {
  if (!bytes || bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return null;
  }
  let at = 2;
  while (at + 4 <= bytes.length) {
    if (bytes[at] !== 0xff) return null;
    const marker = bytes[at + 1];
    // Start of the picture data or end of the file: no more headers.
    if (marker === 0xda || marker === 0xd9) return null;
    const length = (bytes[at + 2] << 8) | bytes[at + 3];
    if (length < 2) return null;
    if (marker === 0xe1 && isExifAt(bytes, at + 4)) {
      return readTiff(
        bytes,
        at + 4 + EXIF_HEADER.length,
        Math.min(bytes.length, at + 2 + length),
      );
    }
    at += 2 + length;
  }
  return null;
};

const ascii = (value) => [...value].map((char) => char.charCodeAt(0) & 0x7f);

// A little-endian TIFF block with the details: the make and model in the
// main directory, the time taken and its offset in the Exif directory.
const tiffWith = ({ takenAt, offset, make, model }) => {
  const main = [];
  if (make)
    main.push({ tag: TAG.make, type: ASCII, data: [...ascii(make), 0] });
  if (model) {
    main.push({ tag: TAG.model, type: ASCII, data: [...ascii(model), 0] });
  }
  const exif = [];
  if (takenAt) {
    exif.push({ tag: TAG.exifVersion, type: UNDEFINED, data: ascii("0231") });
    exif.push({ tag: TAG.takenAt, type: ASCII, data: [...ascii(takenAt), 0] });
    if (offset) {
      exif.push({
        tag: TAG.takenAtOffset,
        type: ASCII,
        data: [...ascii(offset), 0],
      });
    }
  }
  if (!main.length && !exif.length) return null;
  if (exif.length) main.push({ tag: TAG.exifIfd, type: LONG, pointer: true });

  const directorySize = (entries) => 2 + entries.length * 12 + 4;
  // Values longer than 4 bytes go after the directory, each starting on an
  // even byte as TIFF readers expect.
  const even = (length) => length + (length % 2);
  const extraSize = (entries) =>
    entries.reduce(
      (sum, entry) =>
        sum +
        (entry.data && entry.data.length > 4 ? even(entry.data.length) : 0),
      0,
    );
  const mainAt = 8;
  const mainExtraAt = mainAt + directorySize(main);
  const exifAt = mainExtraAt + extraSize(main);
  const exifExtraAt = exifAt + directorySize(exif);
  const size = exif.length ? exifExtraAt + extraSize(exif) : exifAt;

  const out = new Uint8Array(size);
  const view = new DataView(out.buffer);
  out.set([0x49, 0x49], 0);
  view.setUint16(2, 42, true);
  view.setUint32(4, mainAt, true);
  const write = (entries, at, extraAt) => {
    let extra = extraAt;
    view.setUint16(at, entries.length, true);
    entries.forEach((entry, index) => {
      const field = at + 2 + index * 12;
      view.setUint16(field, entry.tag, true);
      view.setUint16(field + 2, entry.type, true);
      if (entry.pointer) {
        view.setUint32(field + 4, 1, true);
        view.setUint32(field + 8, exifAt, true);
        return;
      }
      view.setUint32(field + 4, entry.data.length, true);
      if (entry.data.length <= 4) {
        out.set(entry.data, field + 8);
      } else {
        view.setUint32(field + 8, extra, true);
        out.set(entry.data, extra);
        extra += even(entry.data.length);
      }
    });
    view.setUint32(at + 2 + entries.length * 12, 0, true);
  };
  write(main, mainAt, mainExtraAt);
  if (exif.length) write(exif, exifAt, exifExtraAt);
  return out;
};

/**
 * `jpeg` (a Uint8Array) with `details` written into it as an Exif block, put
 * after the JPEG's JFIF header. The picture itself isn't touched, and no
 * rotation is recorded (the pixels are already the right way up). Returns
 * `jpeg` as it was when it isn't a JPEG or there's nothing to write.
 */
export const withPhotoDetails = (jpeg, details) => {
  if (!jpeg || jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== 0xd8) {
    return jpeg;
  }
  const tiff = details ? tiffWith(details) : null;
  if (!tiff) return jpeg;
  const length = 2 + EXIF_HEADER.length + tiff.length;
  if (length > 0xffff) return jpeg;
  const segment = new Uint8Array(2 + length);
  segment.set([0xff, 0xe1, length >> 8, length & 0xff, ...EXIF_HEADER], 0);
  segment.set(tiff, 4 + EXIF_HEADER.length);

  let at = 2;
  if (jpeg[2] === 0xff && jpeg[3] === 0xe0) {
    at = 4 + ((jpeg[4] << 8) | jpeg[5]);
    if (at > jpeg.length) return jpeg;
  }
  const out = new Uint8Array(jpeg.length + segment.length);
  out.set(jpeg.subarray(0, at), 0);
  out.set(segment, at);
  out.set(jpeg.subarray(at), at + segment.length);
  return out;
};
